import { Constant } from "@pdfking/tfunc"

import type { NetworkProgress, NetworkTrackingCallback } from "#public"

/**
 * XMLHttpRequest-based fetch with upload and download progress tracking.
 *
 * Alternative to ProgressFetch using XHR for upload progress support.
 *
 * @param url - URL to fetch
 * @param options - Request options (data, headers, withCredentials)
 * @param progress - Progress tracking callbacks for upload and download
 * @returns Promise resolving to Response with tracked stream
 *
 * @example
 * ```ts
 * import { ProgressFetchXHR } from "@pdfking/tfunc"
 *
 * const response = await ProgressFetchXHR("https://example.com/upload", {
 *   data: formData,
 *   withCredentials: true
 * }, {
 *   upload: ({ done, total }) => console.log(`Uploading: ${done}/${total}`),
 *   download: ({ done, total }) => console.log(`Downloading: ${done}/${total}`)
 * })
 * ```
 */
const allowBody = (status: number): boolean =>
  !((status >= 100 && status < 200) || status === 204 || status === 304)

const getHeaders = (xhttp: XMLHttpRequest): HeadersInit => {
  const headers: Record<string, string> = {}
  const raw = xhttp.getAllResponseHeaders().trim()
  const lines = raw.split(/[\r\n]+/)
  for (const line of lines) {
    const parts = line.split(": ")
    const key = parts.shift()
    if (key) headers[key.toLowerCase()] = parts.join(": ")
  }
  return headers
}

export const ProgressFetchXHR = (
  url: string,
  {
    data,
    headers,
    withCredentials,
  }: {
    data?: any
    headers?: HeadersInit
    withCredentials?: boolean
  } = {},
  { download, upload }: NetworkProgress = {}
): Promise<Response> => {
  let responseHeaders: HeadersInit | undefined
  let downloadTotal: number | undefined

  const setHeaders = (xhttp: XMLHttpRequest): HeadersInit => {
    if (!responseHeaders) {
      responseHeaders = getHeaders(xhttp)
      const cl = (responseHeaders as Record<string, string>)["content-length"]
      if (cl) downloadTotal = parseInt(cl, 10)
    }
    return responseHeaders
  }

  return new Promise<Response>((resolve, reject) => {
    const xhttp = new XMLHttpRequest()
    xhttp.responseType = "arraybuffer"

    const stream = new ReadableStream<Uint8Array>({
      cancel() {
        xhttp.abort()
      },
      start(controller) {
        const trackEvents = (
          target: XMLHttpRequest | XMLHttpRequestUpload,
          fn: NetworkTrackingCallback,
          getTotal: () => number | undefined
        ) => {
          const events: Array<keyof XMLHttpRequestEventMap> = [
            "loadstart",
            "progress",
            "load",
            "loadend",
          ]
          for (const ev of events) {
            target.addEventListener(ev, evt => {
              const { loaded: done, total } = evt as ProgressEvent
              fn({
                done,
                total: getTotal() ?? (total || done),
              })
            })
          }
        }

        if (download) trackEvents(xhttp, download, () => downloadTotal)
        if (upload) trackEvents(xhttp.upload, upload, Constant(undefined))

        xhttp.onerror = () => {
          const error = new Error("Network error")
          controller.error(error)
          reject(error)
        }

        // Data arrives all-at-once with arraybuffer in XHR
        xhttp.onload = () => {
          controller.enqueue(new Uint8Array(xhttp.response))
          controller.close()
        }

        xhttp.onreadystatechange = () => {
          if (xhttp.readyState === xhttp.HEADERS_RECEIVED) {
            resolve(
              new Response(allowBody(xhttp.status) ? stream : undefined, {
                headers: setHeaders(xhttp),
                status: xhttp.status,
                statusText: xhttp.statusText,
              })
            )
          }
        }

        xhttp.open(data ? "POST" : "GET", url, true)
        xhttp.withCredentials = !!withCredentials

        new Headers(headers).forEach((value, key) => xhttp.setRequestHeader(key, value))

        try {
          xhttp.send(data)
        } catch (err) {
          reject(err)
        }
      },
    })
  })
}
